import { useEffect, useState } from 'react';
import { useAppConstantsContext } from '@/context/AppConstants';
import { FamilySearchRelative } from '@/familyTree/useFamilySearch/types';
import { PostIndi } from '@/familyTree/useFamilyTree/constants';
import { AncestorReportData } from '@/reports/ancestor/constants';
import { Text, styled } from '@/theme/index';
import { formatFamilySearchPersonData, FamilySearchPersonData } from './utils';
import PossibleRelative from '../PossibleRelative/PossibleRelative';

const StyledStack = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',
  flexDirection: 'row',
  rowGap: '20px',
  columnGap: '16px',
  paddingTop: '20px',
});

const StyledImg = styled('img')({
  margin: '20px auto 0',
});

interface SearchResultsProps {
  closeMenu: () => void;
  enableRelativeOnClick: boolean;
  familySearchRelatives: FamilySearchPersonData[];
  patchMemberFromNodeClick: (node: PostIndi) => void;
  setRenderForm?: React.Dispatch<React.SetStateAction<boolean>>;
  createAncestorReportClick: (
    data: AncestorReportData,
    setLoadingCallback: React.Dispatch<React.SetStateAction<boolean>>
  ) => void;
}

type FamilySearchPersonDictionary = Record<
  string,
  {
    raw: FamilySearchPersonData;
    formatted: FamilySearchRelative;
  }
>;

const SearchResults = ({
  closeMenu,
  setRenderForm,
  enableRelativeOnClick,
  familySearchRelatives,
  patchMemberFromNodeClick,
  createAncestorReportClick,
}: SearchResultsProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { images } = useAppConstantsContext();
  const { familyTreeImages } = images;

  useEffect(() => {
    if (setRenderForm) {
      setTimeout(() => {
        // Need to do this in order to render the form when the relatives has been rendered first
        setRenderForm(true);
      }, 1000);
    }
  }, []);

  const handleAncestorReportClick = (data: AncestorReportData) =>
    createAncestorReportClick(data, setLoading);

  const familySearchRelativesDictionary = familySearchRelatives.reduce(
    (acc: FamilySearchPersonDictionary, person) => {
      const formattedFamilySearchPerson = formatFamilySearchPersonData(person);

      acc[formattedFamilySearchPerson.family_search_id] = {
        raw: person,
        formatted: formattedFamilySearchPerson,
      };
      return acc;
    },
    {}
  );

  const renderPossibleRelatives = () => {
    const possibleRelatives = [];

    for (const [key, value] of Object.entries(
      familySearchRelativesDictionary
    )) {
      const { formatted } = value;

      possibleRelatives.push(
        <PossibleRelative
          key={key}
          relative={formatted}
          closeMenu={closeMenu}
          relativesFrom="search-add-relatives"
          patchMemberFromNodeClick={patchMemberFromNodeClick}
          enableRelativeOnClick={enableRelativeOnClick}
          handleAncestorReportClick={handleAncestorReportClick}
          isLoading={loading}
          isFamilySearch
        />
      );
    }

    return possibleRelatives;
  };

  return (
    <>
      <Text variant="h4" data-cy="family-search-results">
        Results {`(${renderPossibleRelatives().length})`}
      </Text>
      <StyledStack>{renderPossibleRelatives()}</StyledStack>
      <StyledImg
        src={familyTreeImages?.imgFamilySearchIcon}
        alt="Family Search"
      />
    </>
  );
};

export default SearchResults;
